import { parseMoney } from '@ims/core'
import { Field, Input, cx } from '@ims/ui'
import { useState } from 'react'
import type { BillDiscountMode } from './lib/cart.ts'
import {
  endsWithPercent,
  parsePercent,
  percentText,
  type LineDiscountMode,
} from './lib/discount-mode.ts'

// Discount boxes that take rupees, a percent or (the bill) the final amount
// (docs/plans/discount-modes.md). The cart keeps the percent or the final amount and works
// out the paisa; only the paisa is ever stored.

function amountText(paisa: number | null): string {
  return paisa === null ? '' : (paisa / 100).toFixed(2)
}

function amountOf(text: string): number | null {
  try {
    return text.trim() === '' ? null : parseMoney(text)
  } catch {
    return null
  }
}

/**
 * The text a box shows for `value`, kept as typed while it still means the same number
 * (the pattern of MoneyInput): "12.5" is not rewritten to "12.50" mid-typing.
 */
function useBoxText(value: number | null, mode: string, show: (v: number | null) => string) {
  const read = (t: string) => (mode === 'percent' ? parsePercent(t) : amountOf(t))
  const [text, setText] = useState(show(value))
  const [last, setLast] = useState<[number | null, string]>([value, mode])
  if (value !== last[0] || mode !== last[1]) {
    setLast([value, mode])
    if (mode !== last[1] || read(text) !== value) setText(show(value))
  }
  return [text, setText] as const
}

export function LineDiscountInput({
  description,
  mode,
  discountPaisa,
  percentBp,
  onAmount,
  onPercent,
  onMode,
  className,
}: {
  description: string
  mode: LineDiscountMode
  discountPaisa: number
  percentBp: number | null
  onAmount: (paisa: number) => void
  onPercent: (bp: number) => void
  /** The switch: the parent converts the line and remembers the choice. */
  onMode: (mode: LineDiscountMode) => void
  className?: string
}) {
  const value = mode === 'percent' ? percentBp || null : discountPaisa || null
  const show = (v: number | null) =>
    v === null ? '' : mode === 'percent' ? percentText(v) : amountText(v)
  const [text, setText] = useBoxText(value, mode, show)
  const percent = mode === 'percent'
  return (
    <div
      className={cx(
        'flex items-center rounded-xl border border-border bg-surface focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/30',
        className,
      )}
    >
      <Input
        aria-label={`Discount on ${description}`}
        inputMode="decimal"
        placeholder="Discount"
        className="min-w-0 rounded-none border-0 bg-transparent px-2! text-right focus:ring-0"
        value={text}
        onChange={(e) => {
          const typed = e.target.value
          if (!percent && endsWithPercent(typed)) {
            setText(typed.replace(/%\s*$/, ''))
            onMode('percent')
            onPercent(parsePercent(typed) ?? 0)
            return
          }
          setText(typed)
          if (percent) onPercent(parsePercent(typed) ?? 0)
          else onAmount(amountOf(typed) ?? 0)
        }}
      />
      <button
        type="button"
        aria-label={`Discount on ${description} in ${percent ? 'percent' : 'rupees'}, change`}
        title={percent ? 'In percent: tap for rupees' : 'In rupees: tap for percent'}
        onClick={() => onMode(percent ? 'amount' : 'percent')}
        className={cx(
          'mr-1 flex h-7 shrink-0 items-center rounded-lg px-2 text-xs font-bold pos:h-9 pos:px-3',
          percent ? 'bg-primary-light text-primary' : 'bg-soft text-muted hover:text-ink',
        )}
      >
        {percent ? '%' : 'Rs'}
      </button>
    </div>
  )
}

const BILL_MODES: { value: BillDiscountMode; label: string; title: string }[] = [
  { value: 'percent', label: '%', title: 'A percent of the bill' },
  { value: 'amount', label: 'Rs', title: 'An amount off the bill' },
  { value: 'final', label: 'Final', title: 'Type what the customer pays' },
]

export function BillDiscountField({
  label,
  mode,
  amountPaisa,
  percentBp,
  finalPaisa,
  hint,
  onMode,
  onAmount,
  onPercent,
  onFinal,
  withFinal = true,
}: {
  label: string
  mode: BillDiscountMode
  amountPaisa: number
  percentBp: number | null
  finalPaisa: number | null
  /** What the discount comes to, under the box. */
  hint?: string
  onMode: (mode: BillDiscountMode) => void
  onAmount: (paisa: number) => void
  onPercent: (bp: number) => void
  onFinal: (paisa: number | null) => void
  withFinal?: boolean
}) {
  const value =
    mode === 'percent' ? percentBp || null : mode === 'final' ? finalPaisa : amountPaisa || null
  const show = (v: number | null) =>
    v === null ? '' : mode === 'percent' ? percentText(v) : amountText(v)
  const [text, setText] = useBoxText(value, mode, show)
  const modes = withFinal ? BILL_MODES : BILL_MODES.filter((m) => m.value !== 'final')
  return (
    <Field
      label={label}
      hint={hint}
      labelAction={
        <div
          role="radiogroup"
          aria-label={`${label} as`}
          className="flex gap-0.5 rounded-xl bg-soft p-0.5"
        >
          {modes.map((m) => (
            <button
              key={m.value}
              type="button"
              role="radio"
              aria-checked={mode === m.value}
              title={m.title}
              onClick={() => onMode(m.value)}
              className={cx(
                'h-7 min-w-10 rounded-lg px-2.5 text-xs font-semibold pos:h-10 pos:min-w-12',
                mode === m.value
                  ? 'bg-surface text-primary shadow-card ring-1 ring-border'
                  : 'text-muted hover:text-ink',
              )}
            >
              {m.label}
            </button>
          ))}
        </div>
      }
    >
      {(id) => (
        <div className="relative">
          {mode !== 'percent' && (
            <span
              aria-hidden
              className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm font-medium text-muted"
            >
              Rs.
            </span>
          )}
          <Input
            id={id}
            inputMode="decimal"
            placeholder={
              mode === 'final' ? 'What the customer pays' : mode === 'percent' ? '0' : '0.00'
            }
            className={cx(mode !== 'percent' && 'pl-10', mode === 'percent' && 'pr-8')}
            value={text}
            onChange={(e) => {
              const typed = e.target.value
              if (mode !== 'percent' && endsWithPercent(typed)) {
                setText(typed.replace(/%\s*$/, ''))
                onMode('percent')
                onPercent(parsePercent(typed) ?? 0)
                return
              }
              setText(typed)
              if (mode === 'percent') onPercent(parsePercent(typed) ?? 0)
              else if (mode === 'final') onFinal(amountOf(typed))
              else onAmount(amountOf(typed) ?? 0)
            }}
          />
          {mode === 'percent' && (
            <span
              aria-hidden
              className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm font-medium text-muted"
            >
              %
            </span>
          )}
        </div>
      )}
    </Field>
  )
}
